<template>
  <div>
    <v-text-field
      v-model="search"
      solo
      autofocus
      class="input-search"
      clearable
      :placeholder="placeholderSearch"
      hide-details>
      <template #append-outer>
        <slot name="appendOuterContent" />
      </template>
    </v-text-field>
    <p
      v-if="shouldShowNoProductsMsg"
      class="my-12 mx-2">
      {{ noProductsMsg }}
    </p>
    <Results
      :items="products"
      @addToSelected="productSelected"
      @onIntersect="onIntersect" />
  </div>
</template>
<script>
import SearchProducts from '@/services/graphql/searching/searchProducts';
import productHeaders from '@/constants/productHeaders';
import {
  mapMutations, mapState, mapActions, mapGetters,
} from 'vuex';
import {
  debounce, uniqBy,
} from 'lodash';
import { DEBOUNCE_TIME_FOR_SEARCHING } from '@/constants';
import AsyncSearching from '@/mixins/AsyncSearching';
import { v4 as uuid } from 'uuid';
import XRay from '@/utils/xRay/XRay';
import { SEARCH_PRODUCT_REF_V3 } from '@/constants/searching/searchingAsyncV2';
import { SORTING_MODE_LAST_ACCESSED } from '@/constants/sortable';
const xRay = new XRay();
export default {
  name: 'CreateProductSearchHandler',
  components: {
    Results: () => import('@/components/Product/ProductCell/ProductCellFieldProduct/ProductCellFieldProductAutocomplete/ProductCellFieldProductAutocompleteResults'),
  },
  mixins: [AsyncSearching],
  props: {
    value: {
      type: undefined,
      default: null,
    },
    dialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selected: null,
      search: '',
      isProductFromSearch: false,
      products: [],
      scrollID: null,
      isSearching: true,
    };
  },
  computed: {
    ...mapState(['showSpinner']),
    ...mapState('StoredProducts', ['recentlyAccessedCollection']),
    ...mapState('Projects', {
      activeProjects: 'active',
    }),
    ...mapState('ProjectDetails', ['detailProjectData']),
    ...mapState('Workspace', ['activeWorkspaceId']),
    ...mapGetters({
      lastProductRendering: 'StoredProducts/lastProductRendering',
    }),
    valueIds() {
      const { value } = this;
      let valueArr = value && Array.isArray(value) ? value : [value];
      return valueArr?.map((e => e?.id)) || [];
    },
    isAccessPreloadingProducts() {
      return this.preloadingProducts.length;
    },
    preloadingProducts() {
      const {
        filteredLastEditedProducts: products,
        filteredRecentlyAccessedCollectionProducts: productCollections,
      } = this;
      return uniqBy([...products, ...productCollections], 'id');
    },
    filteredLastEditedProducts() {
      const { sourceLibraryId = '', valueIds } = this;
      return this.lastProductRendering.reduce((result, current) => {
        const { id = '' } = current || {
        };
        const isSourceLibraryId = current?.libraryId === sourceLibraryId;
        const res = this.parserProduct(current, isSourceLibraryId);
        return res && !valueIds.includes(id) ? [...result, res] : result;
      }, []);
    },
    filteredRecentlyAccessedCollectionProducts() {
      const { sourceLibraryId = '', valueIds } = this;
      const {
        products,
        collectionName,
        libraryId,
        collectionId: collection,
      } = this.recentlyAccessedCollection;
      if (!sourceLibraryId || sourceLibraryId === libraryId) {
        return products.reduce((result, current) => {
          const { id = '' } = current || {
          };
          const isSourceLibraryId = libraryId === sourceLibraryId;
          const res = this.parserProduct(current, isSourceLibraryId);
          return res && !valueIds.includes(id) ? [...result, {
            ...res, collectionName, collection,
          }] : result;
        }, []);
      }
      return [];
    },
    sourceLibraryId() {
      return this.detailProjectData?.sourceLibraryId || '';
    },
    lastProductId() {
      return this.products[this.products.length - 1]?.id;
    },
    filteredProducts() {
      const { value, valueIds } = this;
      if (value) {
        return this.products.filter(item => !valueIds.includes(item.id));
      }
      return this.products;
    },
    placeholderSearch() {
      return 'Find an existing Product record from Collections in the Workspace';
    },
    shouldShowNoProductsMsg() {
      return !this.products?.length && !this.isSearching;
    },
    noProductsMsg() {
      if (this.search) {
        return 'No products found matching the search query.';
      } else {
        return 'There are no existing products from collections in this workspace.';
      }
    },
  },
  watch: {
    value: {
      handler(val) {
        this.selected = val;
      },
      immediate: true,
    },
    dialog: {
      handler(val) {
        const {
          search,
          searchNewSet,
          isAccessPreloadingProducts,
          preloadingProducts,
        } = this;
        if (!val) {
          this.onCloseClear();
          return;
        }
        if (search) {
          this.search = '';
        } else {
          if (isAccessPreloadingProducts) {
            this.isProductFromSearch = false;
            this.products = preloadingProducts;
          } else
            searchNewSet();
        }
      },
      immediate: true,
    },
    search: debounce(function () {
      this.searchNewSet();
    }, DEBOUNCE_TIME_FOR_SEARCHING),
  },
  beforeDestroy() {
    this.onCloseClear();
  },
  methods: {
    ...mapMutations(['spinner']),
    ...mapActions(['handleError']),
    ...mapActions('Projects', ['getProjects']),
    parserProduct(product, isSourceLibraryId) {
      const { IMAGE, PRODUCT_TYPE, MODEL, MANUFACTURER, DESCRIPTION } = productHeaders;
      const { VERIFIED, CHILD_VERIFIED, VERIFIED_PAGE_ID, VERIFIED_PAGE_NAME } = productHeaders;
      const { sourceLibraryId = '' } = this;
      const {
        collectionName = '',
        id = '',
        [IMAGE]: attachment = [],
        [PRODUCT_TYPE]: productType = [],
        [MODEL]: model = '',
        [MANUFACTURER]: manufacturer = '',
        [DESCRIPTION]: shortDescription = '',
        [VERIFIED]: isVerified = false,
        [CHILD_VERIFIED]: childVerified = '',
        [VERIFIED_PAGE_ID]: verifiedPageId = '',
        [VERIFIED_PAGE_NAME]: verifiedPageName = '',
        collectionId: collection = '',
      } = product || {
      };
      const isPassProductSourceLibrary = !sourceLibraryId || isSourceLibraryId;
      const parsedProduct = {
        id,
        attachment,
        productType,
        model,
        collectionName,
        manufacturer,
        shortDescription,
        collection,
        isVerified,
        childVerified,
        verifiedPageId,
        verifiedPageName,
      };
      return !isPassProductSourceLibrary ? null : product.hasOwnProperty('SK') ? parsedProduct : product;
    },
    async productSelected(product) {
      this.$emit('searchProductCallback', product);
    },
    async searchNewSet() {
      this.scrollID = null;
      this.isProductFromSearch = true;
      this.products = [];
      this.searchProductRef();
    },
    onCloseClear() {
      this.unsubscribe();
      if (this.isSearching) {
        this.spinner(false);
      }
    },
    async searchProductRef() {
      this.spinner(true);
      this.isSearching = true;
      if (!this.activeProjects?.length) {
        await this.getProjects({
          status: 'active',
          scanIndexForward: false,
          orderBy: SORTING_MODE_LAST_ACCESSED,
          skipWhenFound: true,
          useSpinner: false,
        });
      }
      try {
        const LIMIT = 10;
        this.currentRequestId = uuid();
        const projectId = this.activeProjects.length ? this.activeProjects[0].id : '';
        const { search: query, activeWorkspaceId: workspaceId, scrollID } = this;
        const jsonForRequest = {
          ...(query && {
            query,
          }),
          projectId: projectId,
          limit: LIMIT,
          scrollID,
          workspaceId,
          requestId: this.currentRequestId,
        };
        this.currentParseMethod = this.compareSearchMatches;
        await this.asyncSearchingSubscription({
          callback: this.setSearchResult,
        });
        xRay.startTimeTracker(SEARCH_PRODUCT_REF_V3);
        await SearchProducts['asyncSearchProductRefV3'](jsonForRequest);
      } catch (err) {
        this.isSearching = false;
        this.spinner(false);
        this.handleError(err);
      }
    },
    compareSearchMatches({ data, searchString } = {
    }) {
      if ((searchString || this.search) && searchString !== this.search) {
        return;
      }
      this.setSearchResult({
        data,
      });
    },
    setSearchResult({ data } = {
    }) {
      this.spinner(false);
      const { data: dataProducts, scrollID, queryName } = data || {
      };
      xRay.stopTimeTracker(queryName);
      xRay.sendSegmentsToAws();
      this.products = this.scrollID ? [...this.products, ...dataProducts] : dataProducts;
      this.products = this.lodash.uniqBy(this.products, 'id');
      this.scrollID = scrollID;
      this.isSearching = false;
    },
    onIntersect({ entries, isIntersecting } = {
    }) {
      if (isIntersecting) {
        const productId = entries[0].target.id;
        if (this.scrollID && productId === this.lastProductId) {
          this.searchProductRef();
        }
      }
    },
  },
};
</script>
<style scoped lang="scss">
.input-search {
  align-items: center;
  ::v-deep .v-input__append-outer {
    margin: 0 0 0 10px !important;
  }
}
.v-select-list {
  max-height: 50vh !important;
}
</style>
